import { useEffect, useMemo, useState } from 'react';
import { datalogClient } from '../services/datalogClient';
import { useWorkspaceStore } from '../store/workspace';
import type { GraphPanel, RenderWindowResult } from '../types';
import { useElementSize } from '../lib/useElementSize';

type OverviewScrubberProps = {
  graphs: GraphPanel[];
  focusedGraphId: string | null;
};

const overviewHeight = 36;
const overviewTopPadding = 5;
const overviewPlotHeight = 25;

export function OverviewScrubber({ graphs, focusedGraphId }: OverviewScrubberProps) {
  const session = useWorkspaceStore((state) => state.session)!;
  const visible = useWorkspaceStore((state) => state.visibleTimeRange);
  const setVisible = useWorkspaceStore((state) => state.setVisibleTimeRange);
  const { ref, width } = useElementSize<HTMLDivElement>();
  const [data, setData] = useState<RenderWindowResult | null>(null);
  const graph = graphs.find((candidate) => candidate.id === focusedGraphId) ?? graphs[0];
  const slots = graph?.slots.filter((slot) => slot.channelId) ?? [];
  const channelIds = slots.map((slot) => slot.channelId!);
  const colorById = new Map(slots.map((slot) => [slot.channelId!, slot.color]));

  useEffect(() => {
    if (!channelIds.length || width < 80) {
      setData(null);
      return;
    }
    let active = true;
    void datalogClient
      .renderWindow(
        channelIds,
        { start: session.startTime, end: session.endTime },
        Math.max(120, width),
      )
      .then((result) => {
        if (active) setData(result);
      });
    return () => {
      active = false;
    };
  }, [channelIds.join('|'), session.endTime, session.startTime, width]);

  const paths = useMemo(() => {
    if (!data?.time.length || width < 1) return [];
    return channelIds.flatMap((id) => {
      const values = data.values[id];
      if (!values) return [];
      const finite = Array.from(values).filter(Number.isFinite);
      if (!finite.length) return [];
      const min = Math.min(...finite);
      const max = Math.max(...finite);
      const span = Math.max(0.000001, max - min);
      const duration = Math.max(0.000001, session.endTime - session.startTime);
      let path = '';
      for (let index = 0; index < data.time.length; index += 1) {
        const value = values[index];
        if (!Number.isFinite(value)) continue;
        const x = ((data.time[index] - session.startTime) / duration) * width;
        const y = overviewTopPadding + overviewPlotHeight - ((value - min) / span) * overviewPlotHeight;
        path += `${path ? ' L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`;
      }
      return [{ id, path, color: colorById.get(id) ?? '#94a3b8' }];
    });
  }, [channelIds, colorById, data, session.endTime, session.startTime, width]);

  const duration = Math.max(0.000001, session.endTime - session.startTime);
  const left = ((visible.start - session.startTime) / duration) * 100;
  const right = ((visible.end - session.startTime) / duration) * 100;
  const minimumWindow = Math.max(0.01, duration / 1000);

  return (
    <section className="overview-section" aria-label="Displayed data window">
      <div className="overview-labels">
        <span>DISPLAYED DATA WINDOW</span>
        <span>Controls every graph</span>
      </div>
      <div ref={ref} className="overview-scrubber">
        {paths.length ? (
          <svg viewBox={`0 0 ${Math.max(1, width)} ${overviewHeight}`} preserveAspectRatio="none" aria-hidden="true">
            {paths.map((entry) => (
              <path key={entry.id} d={entry.path} fill="none" stroke={entry.color} strokeWidth="1.1" opacity="0.82" />
            ))}
          </svg>
        ) : (
          <div className="overview-empty">Add a channel to show the displayed data window.</div>
        )}
        <div className="overview-dim left" style={{ width: `${Math.max(0, left)}%` }} />
        <div className="overview-dim right" style={{ left: `${Math.min(100, right)}%` }} />
        <div
          className="overview-window"
          style={{ left: `${Math.max(0, left)}%`, width: `${Math.max(0, Math.min(100, right) - Math.max(0, left))}%` }}
        />
        <input
          className="overview-range start"
          type="range"
          min={session.startTime}
          max={session.endTime}
          step={minimumWindow}
          value={visible.start}
          aria-label="Visible range start"
          onChange={(event) => {
            const start = Number(event.currentTarget.value);
            setVisible({ start: Math.min(start, visible.end - minimumWindow), end: visible.end });
          }}
        />
        <input
          className="overview-range end"
          type="range"
          min={session.startTime}
          max={session.endTime}
          step={minimumWindow}
          value={visible.end}
          aria-label="Visible range end"
          onChange={(event) => {
            const end = Number(event.currentTarget.value);
            setVisible({ start: visible.start, end: Math.max(end, visible.start + minimumWindow) });
          }}
        />
      </div>
      <div className="overview-time-labels">
        <span>{session.startTime.toFixed(2)}s</span>
        <span>{session.endTime.toFixed(2)}s</span>
      </div>
    </section>
  );
}
